import {
	BreakpointSummaryRes,
	ClassifierRes,
	CreateSecurityValueOverrideDto,
	CreateValuationDto,
	CreateValuationInputAdjustmentDto,
	CreateValuationPersonInputDto,
	GetCurrenciesUsedInValuationRes,
	HistoricalAndCustomExchangeRateRes,
	LatestExchangeRateRes,
	PutCustomExchangeRateDto,
	SecurityValueOverrideRes,
	UpdateSecurityValueOverrideDto,
	UpdateValuationDto,
	UpdateValuationInputAdjustmentDto,
	UpdateValuationInputDto,
	UpdateValuationPersonInputDto,
	UpsertValuationDto,
	UpsertValuationInputsDto,
	UpsertValuationInputsRes,
	UpsertValuationPersonInputsDto,
	UpsertValuationPersonInputsRes,
	ValuationInputAdjustmentWithClassifierRes,
	ValuationInputWithAdjustmentsRes,
	ValuationPersonInputRes,
	ValuationRes,
	ValuationWithClassifierRes,
	GetValuationSummaryResultRes,
	StartCalculateValuationRes,
	ValuationComputeProgressRes,
	ValuationComputeResultRefRes,
} from '@dealstack/shared/lib/generated';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import {
	createHeaders,
	createUrl,
	makeRequest,
} from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { getGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { apiClient } from '@dealstack/frontend/api/client';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { createParser } from 'eventsource-parser';

export const getClassifiers = async (signal?: AbortSignal) => {
	const response = await makeRequest<ApiV1.Response<ClassifierRes[]>>(
		{
			endpoint: API_PATHS.CLASSIFIER_GET,
			method: HTTP_METHOD.GET,
		},
		{
			abortSignal: signal,
		}
	);

	return response;
};

export const getValuations = async ({ dealId }: { dealId: string }) => {
	const valuationsResponse = await makeRequest<ApiV1.Response<ValuationWithClassifierRes[]>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_GET, {
			dealId,
		}),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return valuationsResponse.payload ?? [];
};

export const createValuation = async (dealId: string, data: CreateValuationDto) => {
	const response = await makeRequest<ApiV1.Response<ValuationRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_POST, { dealId }),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const updateValuation = async ({
	valuationId,
	dealId,
	data,
}: {
	valuationId: string;
	dealId: string;
	data: UpdateValuationDto;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationWithClassifierRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PATCH, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const upsertValuation = async ({
	valuationId,
	dealId,
	data,
}: {
	valuationId: string;
	dealId: string;
	data: UpsertValuationDto;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationWithClassifierRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PUT, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const deleteValuation = async ({
	valuationId,
	dealId,
}: {
	valuationId: string;
	dealId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_DELETE, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.DELETE,
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const duplicateValuation = async ({
	valuationId,
	dealId,
	newValuationId,
}: {
	valuationId: string;
	dealId: string;
	newValuationId: string;
}) => {
	return await makeRequest<ApiV1.Response<ValuationRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_DUPLICATE_POST, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ newValuationId }),
		customHeaders: {
			'deal-id': dealId,
		},
	});
};

export const getValuationInputs = async ({
	valuationId,
	dealId,
}: {
	valuationId: string;
	dealId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationInputWithAdjustmentsRes[]>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_GET, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const updateValuationInput = async ({
	valuationId,
	valuationInputId,
	dealId,
	data,
	signal,
}: {
	valuationId: string;
	valuationInputId: string;
	dealId: string;
	data: UpdateValuationInputDto;
	signal?: AbortSignal;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationInputWithAdjustmentsRes>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_VALUATIONINPUTID_PATCH,
				{
					dealId,
					valuationId,
					valuationInputId,
				}
			),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(data),
			customHeaders: {
				'deal-id': dealId,
			},
		},
		{ abortSignal: signal }
	);

	return response;
};

export const upsertValuationInputs = async ({
	valuationId,
	dealId,
	data,
}: {
	valuationId: string;
	dealId: string;
	data: UpsertValuationInputsDto;
}) => {
	const response = await makeRequest<ApiV1.Response<UpsertValuationInputsRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_PUT, {
			dealId,
			valuationId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const createValuationInputAdjustment = async ({
	dealId,
	valuationId,
	valuationInputId,
	data,
}: {
	dealId: string;
	valuationId: string;
	valuationInputId: string;
	data: CreateValuationInputAdjustmentDto;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationInputAdjustmentWithClassifierRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_VALUATIONINPUTID_ADJUSTMENT_POST,
			{
				dealId,
				valuationId,
				valuationInputId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const updateValuationInputAdjustment = async ({
	dealId,
	valuationId,
	valuationInputId,
	valuationInputAdjustmentId,
	data,
	signal,
}: {
	valuationId: string;
	valuationInputId: string;
	valuationInputAdjustmentId: string;
	dealId: string;
	data: UpdateValuationInputAdjustmentDto;
	signal?: AbortSignal;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationInputAdjustmentWithClassifierRes>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_VALUATIONINPUTID_ADJUSTMENT_VALUATIONINPUTADJUSTMENTID_PATCH,
				{
					dealId,
					valuationId,
					valuationInputId,
					valuationInputAdjustmentId,
				}
			),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(data),
			customHeaders: {
				'deal-id': dealId,
			},
		},
		{ abortSignal: signal }
	);

	return response;
};

export const deleteValuationInputAdjustment = async ({
	dealId,
	valuationId,
	valuationInputId,
	valuationInputAdjustmentId,
}: {
	valuationId: string;
	valuationInputId: string;
	valuationInputAdjustmentId: string;
	dealId: string;
}) => {
	await makeRequest<ApiV1.Response<undefined>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_INPUT_VALUATIONINPUTID_ADJUSTMENT_VALUATIONINPUTADJUSTMENTID_DELETE,
			{
				valuationId,
				dealId,
				valuationInputId,
				valuationInputAdjustmentId,
			}
		),
		method: HTTP_METHOD.DELETE,
		customHeaders: {
			'deal-id': dealId,
		},
	});
};

export const getValuationCurrencies = async ({
	dealId,
	valuationId,
}: {
	dealId: string;
	valuationId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<GetCurrenciesUsedInValuationRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_CURRENCY_GET, {
			dealId,
			valuationId,
		}),
		customHeaders: {
			'deal-id': dealId,
		},
		method: HTTP_METHOD.GET,
	});

	return response;
};

export const getLatestFXRates = async ({
	baseCurrency,
	currencies,
}: {
	baseCurrency: string;
	currencies: ReadonlyArray<string>;
}) => {
	const response = await makeRequest<ApiV1.Response<LatestExchangeRateRes>>({
		endpoint: API_PATHS.FX_GET,
		queryString: {
			base: baseCurrency,
			currencies,
		},
		method: HTTP_METHOD.GET,
	});

	return response;
};

export const getHistoricalAndCustomFXRates = async ({
	dealId,
	valuationId,
}: {
	dealId: string;
	valuationId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<HistoricalAndCustomExchangeRateRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_FX_GET, {
			dealId,
			valuationId,
		}),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const putHistoricalAndCustomFXRates = async ({
	dealId,
	valuationId,
	data,
}: {
	dealId: string;
	valuationId: string;
	data: PutCustomExchangeRateDto;
}) => {
	const response = await makeRequest<ApiV1.Response<HistoricalAndCustomExchangeRateRes>>({
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_FX_PUT, {
			dealId,
			valuationId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

/**
 * Starts a valuation compute (a Temporal workflow) and returns its workflowId. The caller then opens
 * the SSE stream with that id to receive progress + the terminal result. Each call starts a fresh
 * workflow (and its own Redis progress stream), so a reload/recompute is just a new start. WAT-730.
 */
export const startCalculateValuation = async ({
	valuationId,
	dealId,
}: {
	valuationId: string;
	dealId: string;
}): Promise<StartCalculateValuationRes> => {
	const response = await makeRequest<ApiV1.Response<StartCalculateValuationRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_CALCULATE_VALUATION_START_POST,
			{
				valuationId,
				dealId,
			}
		),
		method: HTTP_METHOD.POST,
		customHeaders: {
			'deal-id': dealId,
		},
	});
	if (!response.payload?.workflowId) {
		throw new Error('Failed to start valuation compute');
	}
	return response.payload;
};

/** The compute itself failed, so replaying the stream can only re-deliver the same error. */
export class ValuationComputeFailedError extends Error {}

/**
 * Runs the calculate-valuation SSE stream to completion: `progress` events feed `onProgress`,
 * and the promise resolves with the terminal `result` event ({ resultId } - the reference all
 * subsequent summary/breakpoint/excel reads take) or rejects on an `error` event or transport
 * failure. Streams the compute identified by `workflowId` (the backend relays it out of Redis).
 * Uses fetch rather than EventSource because the API needs the deal-id header and cookie
 * credentials on a cross-origin call, which EventSource cannot send.
 */
export const streamCalculateValuation = async ({
	valuationId,
	dealId,
	workflowId,
	onProgress,
	signal,
}: {
	valuationId: string;
	dealId: string;
	workflowId: string;
	onProgress?: (progress: ValuationComputeProgressRes) => void;
	signal?: AbortSignal;
}): Promise<ValuationComputeResultRefRes> => {
	const params = {
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_CALCULATE_VALUATION_STREAM_GET,
			{
				valuationId,
				dealId,
			}
		),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
	};
	const headers = await createHeaders(params);
	const url = `${createUrl(params, getGlobalConfig())}?workflowId=${encodeURIComponent(workflowId)}`;
	const response = await apiClient(url, {
		method: HTTP_METHOD.GET,
		headers,
		mode: 'cors',
		signal,
	});
	if (!response.ok || !response.body) {
		throw new Error(`Valuation compute stream failed with status ${response.status}`);
	}

	let result: ValuationComputeResultRefRes | null = null;
	let streamError: Error | null = null;
	const handleEvent = (event: string, data: string): void => {
		if (event === 'progress') {
			onProgress?.(JSON.parse(data) as ValuationComputeProgressRes);
		} else if (event === 'result') {
			result = JSON.parse(data) as ValuationComputeResultRefRes;
		} else if (event === 'error') {
			const { message } = JSON.parse(data) as { message?: string };
			streamError = new ValuationComputeFailedError(message ?? 'Valuation compute failed');
		}
	};

	// Comment-only frames (heartbeats) carry no data field, so the parser never dispatches them.
	const parser = createParser({
		onEvent: (event) => handleEvent(event.event || 'message', event.data),
	});
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	// Cancelling the reader rejects the in-flight read(), so an abort (React Query supersede /
	// unmount, see useCalculationWorkflow) tears the SSE fetch down instead of draining to the
	// terminal event. The backend proxy turns the dropped socket into its upstream abort, and the
	// task-processor cancels the run on res 'close'. WAT-730.
	const onAbort = (): void => {
		void reader.cancel().catch(() => undefined);
	};
	signal?.addEventListener('abort', onAbort, { once: true });
	try {
		for (;;) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			parser.feed(decoder.decode(value, { stream: true }));
			// Resolve as soon as the terminal event arrives instead of waiting for the connection to
			// close: behind a dev proxy / HTTP keep-alive the `done` can lag well past the `result`
			// frame, which would leave the compute pending (stuck on the loading screen).
			if (result || streamError) {
				await reader.cancel().catch(() => undefined);
				break;
			}
		}
	} finally {
		signal?.removeEventListener('abort', onAbort);
	}
	// Check abort before the !result throw, so a cancelled stream is reported as a cancellation (which
	// React Query ignores), not a malformed one.
	if (signal?.aborted) {
		throw new DOMException('Valuation compute stream aborted', 'AbortError');
	}
	if (streamError) {
		throw streamError;
	}
	if (!result) {
		throw new Error('Valuation compute stream ended without a result');
	}
	return result;
};

export const getValuationSummaryResult = async ({
	valuationId,
	dealId,
	resultId,
}: {
	valuationId: string;
	dealId: string;
	resultId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<GetValuationSummaryResultRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_CALCULATE_VALUATION_RESULT_RESULTID_GET,
			{
				dealId,
				valuationId,
				resultId,
			}
		),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const getValuationPersonInputs = async ({
	valuationId,
	dealId,
	personId,
	mipId,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	mipId?: string;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationPersonInputRes[]>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INPUT_GET,
			{
				valuationId,
				dealId,
				personId,
			}
		),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
			'mip-id': mipId ?? null,
		},
	});

	return response;
};

export const createValuationPersonInput = async ({
	valuationId,
	dealId,
	personId,
	data,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	data: CreateValuationPersonInputDto;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationPersonInputRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INPUT_POST,
			{
				valuationId,
				dealId,
				personId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});

	return response;
};

export const updateValuationPersonInput = async ({
	valuationId,
	dealId,
	personId,
	inputId,
	data,
	signal,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	inputId: string;
	data: UpdateValuationPersonInputDto;
	signal?: AbortSignal;
}) => {
	const response = await makeRequest<ApiV1.Response<ValuationPersonInputRes>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INPUT_INPUTID_PATCH,
				{
					valuationId,
					dealId,
					personId,
					inputId,
				}
			),
			method: HTTP_METHOD.PATCH,
			body: JSON.stringify(data),
			customHeaders: {
				'deal-id': dealId,
			},
		},
		{ abortSignal: signal }
	);

	return response;
};

export const upsertValuationPersonInputs = async ({
	valuationId,
	dealId,
	personId,
	mipId,
	data,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	mipId?: string;
	data: UpsertValuationPersonInputsDto;
}) => {
	const response = await makeRequest<ApiV1.Response<UpsertValuationPersonInputsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INPUT_PUT,
			{
				valuationId,
				dealId,
				personId,
			}
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
			// The URL-derived header follows navigation; a request carrying its own target must
			// keep it in BOTH directions. null drops the URL's value, so a target with no
			// programme is never relabelled with the one the URL happens to show by the time
			// the debounced save fires.
			'mip-id': mipId ?? null,
		},
		queryString: {
			mipId,
		},
	});

	return response;
};

export const deleteValuationPersonInput = async ({
	valuationId,
	dealId,
	personId,
	inputId,
	mipId,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	inputId: string;
	mipId?: string;
}) => {
	await makeRequest<undefined>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INPUT_INPUTID_DELETE,
			{
				valuationId,
				dealId,
				personId,
				inputId,
			}
		),
		method: HTTP_METHOD.DELETE,
		customHeaders: {
			'deal-id': dealId,
			'mip-id': mipId ?? null,
		},
	});
};

export const getValuationBreakpointSummary = async ({
	valuationId,
	dealId,
	personId,
	mipId,
	resultId,
}: {
	valuationId: string;
	dealId: string;
	personId: string;
	mipId?: string;
	resultId: string;
}) => {
	return await makeRequest<ApiV1.Response<BreakpointSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_BREAKPOINT_GET,
			{
				valuationId,
				dealId,
				personId,
			}
		),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
			'mip-id': mipId ?? null,
		},
		queryString: {
			resultId,
		},
	});
};

export const createSecurityValueOverride = async ({
	valuationId,
	dealId,
	data,
}: {
	valuationId: string;
	dealId: string;
	data: CreateSecurityValueOverrideDto;
}) => {
	return await makeRequest<ApiV1.Response<SecurityValueOverrideRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_SECURITYVALUEOVERRIDE_POST,
			{
				dealId,
				valuationId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});
};

export const updateSecurityValueOverride = async ({
	valuationId,
	dealId,
	securityValueOverrideId,
	data,
}: {
	valuationId: string;
	dealId: string;
	securityValueOverrideId: string;
	data: UpdateSecurityValueOverrideDto;
}) => {
	return await makeRequest<ApiV1.Response<SecurityValueOverrideRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_SECURITYVALUEOVERRIDE_SECURITYVALUEOVERRIDEID_PATCH,
			{
				dealId,
				valuationId,
				securityValueOverrideId,
			}
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(data),
		customHeaders: {
			'deal-id': dealId,
		},
	});
};

export const deleteSecurityValueOverride = async ({
	valuationId,
	dealId,
	securityValueOverrideId,
}: {
	valuationId: string;
	dealId: string;
	securityValueOverrideId: string;
}) => {
	return await makeRequest<ApiV1.Response<undefined>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_SECURITYVALUEOVERRIDE_SECURITYVALUEOVERRIDEID_DELETE,
			{
				valuationId,
				dealId,
				securityValueOverrideId,
			}
		),
		method: HTTP_METHOD.DELETE,
		customHeaders: {
			'deal-id': dealId,
		},
	});
};

export const downloadInterestEventsExcel = async ({
	dealId,
	valuationId,
	personId,
	resultId,
	securityId,
	trancheKey,
	mipId,
	tDefault,
}: {
	dealId: string;
	valuationId: string;
	personId: string;
	resultId: string;
	securityId: string;
	trancheKey: string;
	mipId?: string;
	tDefault: (key: string) => string;
}) => {
	const globalConfig = getGlobalConfig();
	const params = {
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_PERSON_PERSONID_INTEREST_EVENTS_EXCEL_GET,
			{
				dealId,
				valuationId,
				personId,
			}
		),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
		queryString: {
			resultId,
			securityId,
			trancheKey,
			...(mipId ? { mipId } : {}),
		},
	};
	const headers = await createHeaders(params);
	const url = createUrl(params, globalConfig);
	const res = await apiClient(url, {
		method: params.method,
		headers,
		body: null,
		mode: 'cors',
		redirect: 'follow',
	});

	const disposition = res.headers.get('Content-Disposition');
	let filename = 'interest-events.xlsx';
	if (disposition && disposition.includes('filename=')) {
		const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1?/);
		if (match?.[2]) {
			filename = decodeURIComponent(match[2]);
		}
	}
	if (!res.ok) {
		showToastV2({ title: tDefault('error_codes.130206'), state: 'error' });
		throw new Error('Download failed');
	}
	const blob = await res.blob();
	downloadFileFromBlob(blob, filename);
};

// see if can use api call
export const downloadValuationExcel = async ({
	valuationId,
	dealId,
	resultId,
	tDefault,
}: {
	valuationId: string;
	dealId: string;
	resultId: string;
	tDefault: (key: string) => string;
}) => {
	const globalConfig = getGlobalConfig();
	const params = {
		endpoint: formatUnicorn(API_PATHS.DEAL_DEALID_VALUATION_VALUATIONID_SUMMARY_EXCEL_GET, {
			valuationId,
			dealId,
		}),
		method: HTTP_METHOD.GET,
		customHeaders: {
			'deal-id': dealId,
		},
		queryString: {
			resultId,
		},
	};
	const headers = await createHeaders(params);
	const url = createUrl(params, globalConfig);
	const res = await apiClient(url, {
		method: params.method,
		headers,
		body: null,
		mode: 'cors',
		redirect: 'follow',
	});

	const disposition = res.headers.get('Content-Disposition');
	let filename = 'valuation-excel.xlsx';
	if (disposition && disposition.includes('filename=')) {
		const match = disposition.match(/filename[^;=\n]*=(['"]?)([^'"\n]*)\1?/);
		if (match?.[2]) {
			filename = decodeURIComponent(match[2]);
		}
	}

	if (!res.ok) {
		showToastV2({
			title: tDefault(`error_codes.130206`),
			state: 'error',
		});
		throw new Error('Download failed');
	}

	// Create a stream reader
	const reader = res.body?.getReader();

	if (!window.showSaveFilePicker) {
		const chunks: Uint8Array<ArrayBuffer>[] = [];

		while (reader) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			chunks.push(value);
		}

		// Combine all chunks into a single Blob
		const blob = new Blob(chunks, {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		});

		downloadFileFromBlob(blob, filename);
	} else {
		// Stream file chunks to disk directly if possible
		const fileHandle = await window.showSaveFilePicker({
			suggestedName: filename,
			types: [
				{
					description: 'Excel File',
					accept: {
						'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
							'.xlsx',
						],
					},
				},
			],
		});

		const writable = await fileHandle.createWritable();
		while (reader) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			await writable.write(value);
		}

		await writable.close();
	}
};
